import {FC} from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {TextFieldElement} from 'react-hook-form-mui';
import {Box, Stack} from '@mui/material';

import {
  imageFileTypes,
  ImageUploadField,
  TranslateFieldButton,
} from '@shared/components';
import {resolveEnglishText} from '@shared/helpers';
import {TranslationDict} from '@shared/types';
import {dummyFunc} from '@shared/utils';
import {openAddTranslationsDialog} from '@widgets/common/AddTranslationsDialog';
import {FormValues} from './v8nSchema';

interface Props {
  existingLogoUrl?: string;
  existingBannerUrl?: string;
  nameTranslations: TranslationDict;
  onNameTranslationsChange: (translations: TranslationDict) => void;
  descriptionTranslations: TranslationDict;
  onDescriptionTranslationsChange: (translations: TranslationDict) => void;
}

export const FormFields: FC<Props> = ({
  existingLogoUrl,
  existingBannerUrl,
  nameTranslations,
  onNameTranslationsChange,
  descriptionTranslations,
  onDescriptionTranslationsChange,
}) => {
  const {
    control,
    setValue,
    formState: {dirtyFields},
  } = useFormContext<FormValues>();
  const currentName = useWatch({control, name: 'name'});
  const currentDescription = useWatch({control, name: 'description'});

  return (
    <Stack spacing={6} mt={2}>
      <TextFieldElement name="slug" label="Slug" control={control} autoFocus />

      <Box display="flex" alignItems="center" gap={2}>
        <TextFieldElement
          name="name"
          label="Name"
          control={control}
          sx={{flex: 1}}
        />
        <TranslateFieldButton
          onClick={() => {
            void openAddTranslationsDialog({
              fieldName: 'Name',
              initialTranslations: nameTranslations,
              enTrans: resolveEnglishText(
                nameTranslations,
                currentName,
                !!dirtyFields.name,
              ),
            })
              .then((translations: TranslationDict) => {
                setValue('name', translations.en ?? currentName);
                onNameTranslationsChange(translations);
                return;
              })
              .catch(dummyFunc);
          }}
        />
      </Box>

      <Box display="flex" alignItems="flex-start" gap={2}>
        <TextFieldElement
          name="description"
          label="Description"
          control={control}
          multiline
          rows={4}
          slotProps={{input: {inputComponent: 'textarea'}}}
          sx={{flex: 1}}
        />
        <TranslateFieldButton
          onClick={() => {
            void openAddTranslationsDialog({
              fieldName: 'Description',
              initialTranslations: descriptionTranslations,
              enTrans: resolveEnglishText(
                descriptionTranslations,
                currentDescription,
                !!dirtyFields.description,
              ),
            })
              .then((translations: TranslationDict) => {
                setValue('description', translations.en ?? currentDescription);
                onDescriptionTranslationsChange(translations);
                return;
              })
              .catch(dummyFunc);
          }}
        />
      </Box>

      <TextFieldElement
        name="order"
        label="Order"
        control={control}
        type="number"
        sx={{maxWidth: 160}}
        slotProps={{htmlInput: {min: 0, step: 1}}}
      />

      <Box display="flex" gap={4} alignItems="flex-start" flexWrap="wrap">
        <Stack spacing={1} width={160}>
          <ImageUploadField<FormValues>
            fieldName="logoFile"
            label="Avatar"
            acceptedFileTypes={imageFileTypes}
            existingFileUrl={existingLogoUrl}
            width={160}
            height={160}
            alt="Category avatar"
          />
        </Stack>

        <Stack spacing={1} width={420}>
          <ImageUploadField<FormValues>
            fieldName="bannerFile"
            label="Banner"
            acceptedFileTypes={imageFileTypes}
            existingFileUrl={existingBannerUrl}
            width="100%"
            height={160}
            imageFit="cover"
            alt="Category banner"
          />
        </Stack>
      </Box>
    </Stack>
  );
};
